import React, { useState } from 'react';
import * as Select from '@radix-ui/react-select';
import { User, Check, ChevronDown } from 'lucide-react';

export interface SelectOption {
  id: string;
  name?: string;
  label?: string;
  value: string;
  subtitle?: string;
  avatar?: string;
  icon?: React.ReactNode;
}

interface IconSelectProps<T extends SelectOption> {
  options: T[];
  onSelect: (optionId: string) => void;
  selectedId?: string;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  defaultIcon?: React.ReactNode;
}

/**
 * A reusable selection component that displays a list of options with icons/avatars and details.
 * Can be used for users, roles, or any other selectable items with optional icons.
 * Uses Radix UI's Select component for accessibility and consistent styling.
 */
export const SelectMenu = <T extends SelectOption>({
  options,
  onSelect,
  selectedId,
  placeholder = 'Select option',
  label = 'Select option',
  className = '',
  disabled = false,
  defaultIcon = <User className="h-4 w-4 text-gray-400" />,
}: IconSelectProps<T>) => {
  const [selectedOption, setSelectedOption] = useState(options.find(option => option.id === selectedId));
  return (
    <Select.Root value={selectedOption?.id} onValueChange={(value) => {
      setSelectedOption(options.find(option => option.id === value));
      onSelect(value);
    }} disabled={disabled}>
      <Select.Trigger
        className={`flex items-center space-x-2 text-sm text-gray-400 hover:text-white transition-colors outline-none ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${className}`}
        aria-label={label}
      >
        <div className="h-6 w-6 rounded-full bg-gray-800 flex items-center justify-center">
          {selectedOption?.icon || defaultIcon}
        </div>
        <Select.Value>
          {selectedOption?.label || selectedOption?.name || placeholder}
        </Select.Value>
        {!disabled && (
          <Select.Icon>
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </Select.Icon>
        )}
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className="min-w-[220px] bg-[#1A1F2E] rounded-lg p-1 shadow-xl border border-gray-800"
          position="popper"
          side="bottom"
          sideOffset={5}
          align="start"
          alignOffset={0}
          avoidCollisions={true}
          collisionPadding={8}
        >
          <Select.ScrollUpButton />
          <Select.Viewport>
            <Select.Group>
              <Select.Label className="px-3 py-2 text-xs text-gray-500 font-medium">
                {label}
              </Select.Label>
              {options.map((option) => (
                <Select.Item
                  key={option.id}
                  value={option.id}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:bg-gray-800/50 rounded-md cursor-pointer outline-none data-[highlighted]:bg-gray-800/50"
                >
                  <div className='flex items-center justify-between w-full'>
                    <div className="flex items-center space-x-2">
                      <div className="h-4 w-4 rounded-full bg-gray-800 flex items-center justify-center">
                        {option.avatar ? (
                          <img
                            src={option.avatar}
                            alt={option.name}
                            className="h-4 w-4 rounded-full"
                          />
                        ) : option?.icon || defaultIcon}
                      </div>
                      <div>
                        <div className="font-medium">{option?.name || option?.label}</div>
                        {option.subtitle && (
                          <div className="text-xs text-gray-500">{option.subtitle}</div>
                        )}
                      </div>
                    </div>
                    <Select.ItemIndicator className="h-4 w-4">
                      <Check className="h-4 w-4" />
                    </Select.ItemIndicator>

                  </div>
                </Select.Item>
              ))}
            </Select.Group>
          </Select.Viewport>
          <Select.ScrollDownButton />
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};
